export interface TcfData {
  cmpId?: number;
  cmpStatus?: string;
  eventStatus?: string;
  gdprApplies?: boolean;
  tcString?: string;
  listenerId?: number;
  purpose?: { consents?: Record<number, boolean> };
  vendor?: { consents?: Record<number, boolean> };
  publisher?: { restrictions?: Record<number, Record<number, number>> };
}
export type TcfCallback = (data: TcfData | undefined, success: boolean) => void;
export type TcfApi = (command: string, version: number, callback: TcfCallback | ((success: boolean) => void), parameter?: number) => void;
export interface GooglePrivacyApi {
  callbackQueue: { push: (callback: Record<string, () => void>) => unknown };
  showRevocationMessage?: () => void;
}
declare global {
  interface Window {
    __tcfapi?: TcfApi;
    googlefc?: GooglePrivacyApi;
    adsbygoogle?: Array<Record<string, never>> & { requestNonPersonalizedAds?: number; pauseAdRequests?: number };
  }
  interface Navigator { readonly globalPrivacyControl?: boolean; }
}

/**
 * Conservative gate, not a replacement for Google's full TCF evaluation.
 * Require explicit storage and Google vendor consent in every region. AdSense
 * evaluates other purposes, restrictions and partners. We request only
 * non-personalized ads; declining storage means no ads at all.
 */
export function hasAdvertisingConsent(data: TcfData | undefined, success: boolean): boolean {
  if (!success || !data || data.cmpId !== 300 || data.cmpStatus !== 'loaded') return false;
  if (data.eventStatus !== 'tcloaded' && data.eventStatus !== 'useractioncomplete') return false;
  if (typeof data.gdprApplies !== 'boolean' || !data.tcString?.trim()) return false;
  if (data.purpose?.consents?.[1] !== true || data.vendor?.consents?.[755] !== true) return false;
  const restriction = data.publisher?.restrictions?.[1]?.[755];
  return restriction === undefined || restriction === 1;
}

export function observeTcfConsent(api: TcfApi, onChange: (allowed: boolean) => void, onReady: () => void = () => undefined): () => void {
  let active = true;
  let listenerId: number | undefined;
  const remove = () => {
    if (listenerId === undefined) return;
    try { api('removeEventListener', 2, () => undefined, listenerId); } catch { /* Fail closed. */ }
  };
  try {
    api('addEventListener', 2, (data: TcfData | undefined, success: boolean) => {
      listenerId = data?.listenerId ?? listenerId;
      if (!active) { remove(); return; }
      if (success && data?.cmpStatus === 'loaded') onReady();
      onChange(hasAdvertisingConsent(data, success));
    });
  } catch { onChange(false); }
  return () => { active = false; remove(); };
}
